import { Bot, KeyRound, Plus } from 'lucide-react'
import { useState } from 'react'

import { useCompanies } from '@/features/companies/api'
import type { AgentRead, AgentTokenCreated, AgentTokenRead } from '@/shared/api/contracts'
import { CopyButton } from '@/shared/components/common/CopyButton'
import { PageHeader } from '@/shared/components/common/PageHeader'
import { RelativeTime } from '@/shared/components/common/RelativeTime'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { Button, Input, Switch } from '@/shared/components/ui'
import { DEFAULT_LOCALE, useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { CompanyListItem, Uuid } from '@/shared/types/domain'

import {
  useAgents,
  useCreateAgent,
  useCreateAgentToken,
  useRevokeAgentToken,
  useSetUserCompanies,
  useUpdateUser,
} from './api'
import { SettingsNav } from './components/SettingsNav'
import { UserCompaniesSheet } from './components/UserCompaniesSheet'
import type { SettingsUser } from './types'

/**
 * `/settings/agentes` — los procesos que crean y cierran tareas por la API.
 *
 * Un agente es un usuario `tech_member` que entra con un token en vez de un
 * login, y que sólo puede trabajar tareas (`AGENT_ROUTES` en el backend). Esta
 * pantalla hace tres cosas: darlo de alta con sus empresas, emitirle tokens y
 * revocarlos. Desactivarlo corta todos sus tokens de una.
 *
 * **El token se ve una sola vez**, justo después de emitirlo. En la base queda
 * el hash; si se pierde, se emite otro y se revoca el viejo.
 */
export function AgentsPage() {
  const t = useT()
  const agents = useAgents()
  const companies = useCompanies()
  const create = useCreateAgent()
  const update = useUpdateUser()
  const setCompanies = useSetUserCompanies()

  const [creando, setCreando] = useState(false)
  const [editing, setEditing] = useState<AgentRead | null>(null)

  const all = agents.data ?? []
  const companyList = companies.data ?? []

  return (
    <div className="flex min-h-dvh flex-col">
      <PageHeader
        title={t('settings.agents.title')}
        description={t('settings.agents.description')}
        actions={
          <Button variant="primary" onClick={() => setCreando(true)} disabled={creando}>
            <Plus size={13} aria-hidden="true" />
            {t('settings.agents.create')}
          </Button>
        }
      >
        <SettingsNav />
      </PageHeader>

      <div className="flex flex-1 flex-col gap-4 px-4 py-3">
        {creando ? (
          <NewAgentForm
            companies={companyList}
            pending={create.isPending}
            onCancel={() => setCreando(false)}
            onCreate={(input) =>
              create.mutate(input, {
                onSuccess: () => setCreando(false),
              })
            }
          />
        ) : null}

        {agents.isPending ? (
          <SkeletonRows rows={3} columns={[0.3, 0.3, 0.2]} withSpine={false} />
        ) : agents.isError ? (
          <ErrorState error={agents.error} onRetry={() => void agents.refetch()} />
        ) : all.length === 0 && !creando ? (
          <EmptyState
            title={t('settings.agents.emptyTitle')}
            description={t('settings.agents.emptyDescription')}
          />
        ) : (
          <ul aria-label={t('settings.agents.listAria')} className="flex flex-col gap-3">
            {all.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                companies={companyList}
                pending={update.isPending}
                onToggleActive={(isActive) =>
                  update.mutate({ userId: agent.id, patch: { is_active: isActive } })
                }
                onEditCompanies={() => setEditing(agent)}
              />
            ))}
          </ul>
        )}

        <UsageHint />
      </div>

      {editing ? (
        <UserCompaniesSheet
          user={comoUsuario(editing)}
          companies={companyList}
          pending={setCompanies.isPending}
          onCancel={() => setEditing(null)}
          onSave={(companyIds) => {
            setCompanies.mutate(
              { userId: editing.id, companyIds },
              { onSuccess: () => setEditing(null) },
            )
          }}
        />
      ) : null}
    </div>
  )
}

/** El panel de empresas es el de los usuarios: un agente ES un usuario. */
function comoUsuario(agent: AgentRead): SettingsUser {
  return {
    id: agent.id,
    email: agent.email,
    display_name: agent.display_name,
    role: 'tech_member',
    is_active: agent.is_active,
    client_company_id: null,
    company_ids: agent.company_ids,
    locale: DEFAULT_LOCALE,
    last_login_at: null,
  }
}

/* ------------------------------------------------------------ alta -- */

interface NewAgentFormProps {
  companies: readonly CompanyListItem[]
  pending: boolean
  onCancel: () => void
  onCreate: (input: { display_name: string; company_ids: Uuid[] }) => void
}

function NewAgentForm({ companies, pending, onCancel, onCreate }: NewAgentFormProps) {
  const t = useT()
  const [nombre, setNombre] = useState('')
  const [empresas, setEmpresas] = useState<Uuid[]>([])

  const toggle = (id: Uuid) =>
    setEmpresas((actual) =>
      actual.includes(id) ? actual.filter((e) => e !== id) : [...actual, id],
    )

  return (
    <form
      aria-label={t('settings.agents.create')}
      className="hairline flex flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (nombre.trim()) onCreate({ display_name: nombre.trim(), company_ids: empresas })
      }}
    >
      <label className="flex flex-col gap-1 text-2xs text-text-3">
        {t('settings.agents.nameLabel')}
        <Input
          autoFocus
          value={nombre}
          maxLength={80}
          placeholder={t('settings.agents.namePlaceholder')}
          onChange={(event) => setNombre(event.target.value)}
        />
      </label>

      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-2xs text-text-3">{t('settings.agents.companiesLabel')}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {companies.map((company) => (
            <label key={company.id} className="flex cursor-pointer items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={empresas.includes(company.id)}
                onChange={() => toggle(company.id)}
              />
              <span
                aria-hidden="true"
                className="h-3.5 w-[var(--spine-w)] rounded-full"
                style={{ backgroundColor: `hsl(${String(company.color_hue)} 62% 55%)` }}
              />
              <span className="text-text-1">{company.name}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onCancel}>
          {t('common.action.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={pending || !nombre.trim()}>
          {t('settings.agents.create')}
        </Button>
      </div>
    </form>
  )
}

/* ---------------------------------------------------------- tarjeta -- */

interface AgentCardProps {
  agent: AgentRead
  companies: readonly CompanyListItem[]
  pending: boolean
  onToggleActive: (isActive: boolean) => void
  onEditCompanies: () => void
}

function AgentCard({ agent, companies, pending, onToggleActive, onEditCompanies }: AgentCardProps) {
  const t = useT()
  const issue = useCreateAgentToken()
  const revoke = useRevokeAgentToken()
  const [nombreToken, setNombreToken] = useState('')
  const [recien, setRecien] = useState<AgentTokenCreated | null>(null)

  const nombres = companies
    .filter((company) => agent.company_ids.includes(company.id))
    .map((company) => company.name)

  return (
    <li
      className={cn(
        'hairline flex flex-col rounded-[var(--radius-card)] bg-surface-1',
        agent.is_active ? null : 'opacity-60',
      )}
    >
      <div className="hairline-b flex flex-wrap items-center gap-3 px-3 py-2.5">
        <Bot size={16} aria-hidden="true" className="shrink-0 text-text-3" />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm text-text-1">{agent.display_name}</span>
          <span className="num truncate text-2xs text-text-3">{agent.email}</span>
        </span>

        <button
          type="button"
          onClick={onEditCompanies}
          className="max-w-[320px] truncate text-left text-2xs text-text-2 hover:text-text-1"
          title={nombres.join(' · ')}
          aria-label={t('settings.users.companiesAria', { persona: agent.display_name })}
        >
          {nombres.length > 0 ? (
            nombres.join(' · ')
          ) : (
            <span className="text-text-3">{t('settings.users.noCompanies')}</span>
          )}
        </button>

        <Switch
          checked={agent.is_active}
          disabled={pending}
          label={t('settings.users.activeAria', { persona: agent.display_name })}
          showLabel={false}
          onChange={onToggleActive}
          className="shrink-0"
        />
      </div>

      {recien ? (
        <div role="status" className="hairline-b flex flex-col gap-1.5 bg-surface-2/60 px-3 py-2.5">
          <span className="text-2xs text-text-2">{t('settings.agents.tokenOnce')}</span>
          <div className="flex items-center gap-2">
            <code className="num min-w-0 flex-1 truncate rounded-[var(--radius-control)] bg-bg px-2 py-1 text-xs text-text-1 select-all">
              {recien.token}
            </code>
            <CopyButton value={recien.token} label={t('settings.agents.copyToken')} />
            <Button variant="ghost" onClick={() => setRecien(null)}>
              {t('settings.agents.tokenSaved')}
            </Button>
          </div>
        </div>
      ) : null}

      <ul aria-label={t('settings.agents.tokensAria', { agente: agent.display_name })}>
        {agent.tokens.map((token) => (
          <TokenRow
            key={token.id}
            token={token}
            pending={revoke.isPending}
            onRevoke={() => revoke.mutate({ agentId: agent.id, tokenId: token.id })}
          />
        ))}
      </ul>

      <form
        className="flex items-center gap-2 px-3 py-2"
        onSubmit={(event) => {
          event.preventDefault()
          const name = nombreToken.trim()
          if (!name) return
          issue.mutate(
            { agentId: agent.id, name },
            {
              onSuccess: (creado) => {
                setRecien(creado)
                setNombreToken('')
              },
            },
          )
        }}
      >
        <KeyRound size={13} aria-hidden="true" className="shrink-0 text-text-3" />
        <Input
          value={nombreToken}
          maxLength={80}
          disabled={!agent.is_active}
          placeholder={t('settings.agents.tokenNamePlaceholder')}
          aria-label={t('settings.agents.tokenNameAria', { agente: agent.display_name })}
          onChange={(event) => setNombreToken(event.target.value)}
          className="max-w-[260px]"
        />
        <Button type="submit" disabled={issue.isPending || !nombreToken.trim() || !agent.is_active}>
          {t('settings.agents.issueToken')}
        </Button>
      </form>
    </li>
  )
}

function TokenRow({
  token,
  pending,
  onRevoke,
}: {
  token: AgentTokenRead
  pending: boolean
  onRevoke: () => void
}) {
  const t = useT()
  const revocado = token.revoked_at !== null

  return (
    <li
      className={cn(
        'hairline-b flex flex-wrap items-center gap-3 px-3 py-1.5 text-2xs',
        revocado ? 'text-text-3 line-through decoration-text-3/60' : 'text-text-2',
      )}
    >
      <span className="min-w-[120px] text-text-1">{token.name}</span>
      <span className="num">{token.prefix}…</span>
      <span className="num">
        {t('settings.agents.lastUsed')}{' '}
        {token.last_used_at ? (
          <RelativeTime value={token.last_used_at} />
        ) : (
          t('settings.agents.neverUsed')
        )}
      </span>
      <span className="flex-1" />
      {revocado ? (
        <span className="no-underline">{t('settings.agents.revokedLabel')}</span>
      ) : (
        <Button variant="ghost" disabled={pending} onClick={onRevoke}>
          {t('settings.agents.revoke')}
        </Button>
      )}
    </li>
  )
}

/** Lo mínimo para que alguien conecte un agente sin ir a buscar la doc. */
function UsageHint() {
  const t = useT()
  const base = `${window.location.origin}/api/v1`

  return (
    <section className="hairline flex flex-col gap-2 rounded-[var(--radius-card)] bg-surface-1 p-3">
      <h2 className="font-display text-sm text-text-1">{t('settings.agents.usageTitle')}</h2>
      <p className="text-2xs text-text-3">{t('settings.agents.usageDescription')}</p>
      <pre className="num overflow-x-auto rounded-[var(--radius-control)] bg-bg p-2 text-2xs text-text-2">
        {`curl -X POST ${base}/tasks \\
  -H "Authorization: Bearer pmt_…" -H "Content-Type: application/json" \\
  -d '{"project_id": "<id>", "title": "Deploy de staging"}'

curl -X POST ${base}/tasks/<task_id>/complete -H "Authorization: Bearer pmt_…"`}
      </pre>
    </section>
  )
}
